// Захват микрофона → поток Int16 PCM кадров фиксированного размера + уровень
// сигнала (для индикатора волн). Транспортно-нейтрально: что делать с кадрами
// (отправлять по WS, как кодировать) решает вызывающий код.
//
// Реализация на ScriptProcessorNode: формально он deprecated, но это самый
// надёжный способ получить сырой PCM на iOS Safari, где AudioWorklet всё ещё
// капризен. При желании позже заменим на AudioWorklet за этим же интерфейсом.

export interface AudioCaptureOptions {
  /** Желаемая частота дискретизации отправляемого PCM. */
  targetSampleRate: number
  /** Размер кадра в сэмплах (в целевой частоте). */
  frameSize: number
  /** Кадр готового Int16 PCM. */
  onFrame: (pcm: Int16Array) => void
  /** Текущий уровень сигнала 0..1 (RMS), для визуализации. */
  onLevel?: (level: number) => void
}

export class AudioCapture {
  private ctx: AudioContext | null = null
  private stream: MediaStream | null = null
  private source: MediaStreamAudioSourceNode | null = null
  private processor: ScriptProcessorNode | null = null
  private opts: AudioCaptureOptions
  private running = false

  // Аккумулятор ресэмплированных сэмплов до набора целого кадра.
  private acc: Float32Array
  private accLen = 0

  constructor(opts: AudioCaptureOptions) {
    this.opts = opts
    this.acc = new Float32Array(opts.frameSize)
  }

  get isRunning(): boolean {
    return this.running
  }

  async start(): Promise<void> {
    if (this.running) return

    if (!navigator.mediaDevices?.getUserMedia) {
      throw new MicError(
        'unsupported',
        'Доступ к микрофону недоступен в этом браузере или по незащищённому соединению (нужен HTTPS либо localhost).',
      )
    }

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
        },
        video: false,
      })
    } catch (e) {
      throw mapGetUserMediaError(e)
    }

    // AudioContext создаётся в обработчике пользовательского жеста (тап по
    // кнопке) — это требование iOS Safari.
    const Ctx: typeof AudioContext =
      window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    this.ctx = new Ctx()
    if (this.ctx.state === 'suspended') await this.ctx.resume()

    this.source = this.ctx.createMediaStreamSource(this.stream)

    const bufSize = 4096
    this.processor = this.ctx.createScriptProcessor(bufSize, 1, 1)
    const inputRate = this.ctx.sampleRate
    const ratio = inputRate / this.opts.targetSampleRate

    this.processor.onaudioprocess = (ev) => {
      const input = ev.inputBuffer.getChannelData(0)

      // Уровень сигнала (RMS) для индикатора.
      if (this.opts.onLevel) {
        let sum = 0
        for (let i = 0; i < input.length; i++) sum += input[i] * input[i]
        const rms = Math.sqrt(sum / input.length)
        this.opts.onLevel(Math.min(1, rms * 3))
      }

      // Простой ресэмплинг до целевой частоты (decimation/interpolation).
      for (let i = 0; i < input.length; i += ratio) {
        const sample = input[Math.floor(i)]
        this.acc[this.accLen++] = sample
        if (this.accLen >= this.opts.frameSize) {
          this.opts.onFrame(floatToInt16(this.acc))
          this.accLen = 0
        }
      }
    }

    this.source.connect(this.processor)
    // Подключаем к destination через нулевую громкость — иначе в части
    // браузеров onaudioprocess не вызывается.
    const sink = this.ctx.createGain()
    sink.gain.value = 0
    this.processor.connect(sink)
    sink.connect(this.ctx.destination)

    this.running = true
  }

  stop(): void {
    this.running = false
    this.accLen = 0
    try {
      this.processor?.disconnect()
      this.source?.disconnect()
    } catch {
      /* noop */
    }
    this.processor = null
    this.source = null
    this.stream?.getTracks().forEach((t) => t.stop())
    this.stream = null
    this.ctx?.close().catch(() => {})
    this.ctx = null
    this.opts.onLevel?.(0)
  }
}

export type MicErrorKind = 'unsupported' | 'denied' | 'notfound' | 'unknown'

export class MicError extends Error {
  kind: MicErrorKind
  constructor(kind: MicErrorKind, message: string) {
    super(message)
    this.name = 'MicError'
    this.kind = kind
  }
}

function mapGetUserMediaError(e: unknown): MicError {
  const name = (e as { name?: string })?.name || ''
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return new MicError('denied', 'Доступ к микрофону запрещён. Разреши его в настройках Safari.')
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError') {
    return new MicError('notfound', 'Микрофон не найден.')
  }
  return new MicError('unknown', 'Не удалось получить доступ к микрофону: ' + String((e as Error)?.message ?? e))
}

function floatToInt16(buf: Float32Array): Int16Array {
  const out = new Int16Array(buf.length)
  for (let i = 0; i < buf.length; i++) {
    const s = Math.max(-1, Math.min(1, buf[i]))
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff
  }
  return out
}
